import { IconTypes } from '@/components/Icon'
import { MapperType } from '@/lib/types/utilsT'
import {
  CheckoutActionsTypes,
  DiscountTypeValues,
  PaymentBrandTypes,
  PaymentMethodActionTypes,
  SubscriptionCancelTypes,
  SubscriptionStatusTypes,
} from '@/lib/types/SubscriptionT'
import { DropdownItem } from '@/components/ButtonDropdown'

export type SubscriptionStatusType = MapperType<SubscriptionStatusTypes>

export const SUBSCRIPTION_STATUSES = {
  ACTIVE: 'active',
  DEMO: 'demo',
  GRACE: 'grace',
  CANCELED: 'canceled',
  TRIAL: 'trial',
  // DEACTIVATED: 'deactivated',
  // HOLD: 'hold',
} satisfies SubscriptionStatusType

export const SUBSCRIPTION_STATUS_CONFIGS: Record<
  SubscriptionStatusTypes,
  {
    label: string
    bgClassName: string
    pointClassName: string
    textClassName: string
    description: string
  }
> = {
  [SUBSCRIPTION_STATUSES.ACTIVE]: {
    label: 'Active',
    bgClassName: 'subscription-status-active',
    pointClassName: 'subscription-status-active-dot',
    textClassName: 'text-primary-2',
    description: 'Paid premium subscription with full access to features',
  },
  [SUBSCRIPTION_STATUSES.DEMO]: {
    label: 'Demo',
    bgClassName: 'subscription-status-demo',
    pointClassName: 'subscription-status-demo-dot',
    textClassName: 'text-accent-3',
    description: 'Newly created account or free subscription with limited features',
  },
  [SUBSCRIPTION_STATUSES.GRACE]: {
    label: 'Grace Period',
    bgClassName: 'subscription-status-grace',
    pointClassName: 'subscription-status-grace-dot',
    textClassName: 'text-accent-5',
    description: 'Active subscription with failed payment collection, temporary access maintained',
  },
  [SUBSCRIPTION_STATUSES.CANCELED]: {
    label: 'Canceled',
    bgClassName: 'subscription-status-canceled',
    pointClassName: 'subscription-status-canceled-dot',
    textClassName: 'text-accent-1',
    description: 'User has manually stopped the subscription',
  },
  [SUBSCRIPTION_STATUSES.TRIAL]: {
    label: 'Trial',
    bgClassName: 'subscription-status-trial',
    pointClassName: 'subscription-status-trial-dot',
    textClassName: 'text-accent-4',
    description: 'Trial period with full access to premium features for a limited time',
  },
  // [SUBSCRIPTION_STATUSES.DEACTIVATED]: {
  //   label: 'Deactivated',
  //   bgClassName: 'subscription-status-deactivated',
  //   pointClassName: 'subscription-status-deactivated-dot',
  //   textClassName: 'text-n-4',
  //   description: 'Subscription has been completely deactivated by administrator',
  // },
  // [SUBSCRIPTION_STATUSES.HOLD]: {
  //   label: 'On Hold',
  //   bgClassName: 'subscription-status-hold',
  //   pointClassName: 'subscription-status-hold-dot',
  //   textClassName: 'text-accent-2',
  //   description: 'Pricing changed by admin, awaiting user decision after subscription expiry',
  // },
}

export const ACTIVE_SUBSCRIPTION_STATUSES = [
  SUBSCRIPTION_STATUSES.ACTIVE,
  SUBSCRIPTION_STATUSES.TRIAL,
] as readonly SubscriptionStatusTypes[]

export const UNLIMITED_THRESHOLD = 9999

export const SUBSCRIPTION_SUPER_ADMIN_ACTIONS: {
  label: string
  note: string
  icon: IconTypes
  value: string
  classIcon?: string
}[] = [
  {
    label: 'Cancel Subscription',
    note: 'End current subscription',
    icon: 'close',
    value: 'cancel',
    classIcon: 'fill-red-600',
  },
]

export type CheckoutActionType = MapperType<CheckoutActionsTypes>

export const CHECKOUT_ACTIONS = {
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
  SUBSCRIBE: 'subscribe',
  NO_ACTION: 'no_action',
} satisfies CheckoutActionType

export type SubscriptionCancelType = MapperType<SubscriptionCancelTypes>

export const CANCEL_TYPES = {
  IMMEDIATE: 'immediate',
  END_OF_PERIOD: 'end_of_period',
} satisfies SubscriptionCancelType

export type PaymentMethodActionType = MapperType<PaymentMethodActionTypes>
export const PAYMENT_METHOD_ACTIONS = {
  COPY: 'copy',
  SET_DEFAULT: 'set_default',
  DELETE: 'delete',
} satisfies PaymentMethodActionType

export const PAYMENT_METHOD_ACTIONS_LIST: DropdownItem<PaymentMethodActionTypes>[] = [
  {
    label: 'Set as default',
    value: PAYMENT_METHOD_ACTIONS.SET_DEFAULT,
    classLabel: 'text-primary-1 dark:text-primary-1',
  },
  {
    label: 'Delete',
    value: PAYMENT_METHOD_ACTIONS.DELETE,
    classLabel: 'text-red-500 dark:text-red-400',
  },
]

export type PaymentBrandType = MapperType<PaymentBrandTypes>
export const PAYMENT_BRAND = {
  VISA: 'visa',
  MASTERCARD: 'mastercard',
  AMERICAN_EXPRESS: 'american_express',
  DISCOVER_GLOBAL_NETWORK: 'discover_global_network',
} satisfies PaymentBrandType

export const PAYMENT_BRAND_CONFIG: Record<PaymentBrandTypes, { img: string; title: string }> = {
  [PAYMENT_BRAND.VISA]: {
    title: 'Visa',
    img: '/payment-methods/visa.svg',
  },
  [PAYMENT_BRAND.MASTERCARD]: {
    title: 'Master card',
    img: '/payment-methods/mastercard.svg',
  },
  [PAYMENT_BRAND.AMERICAN_EXPRESS]: {
    title: 'American Express',
    img: '/payment-methods/american-express.svg',
  },
  [PAYMENT_BRAND.DISCOVER_GLOBAL_NETWORK]: {
    title: 'Discover Global Network',
    img: '/payment-methods/discover.svg',
  },
}

export type DiscountType = MapperType<DiscountTypeValues>

export const DISCOUNT_TYPES = {
  PERCENT: 'percent',
  AMOUNT: 'amount',
} satisfies DiscountType
